import './admin-avg-broadcast.css';
import { state } from '../shared/state';
import { $, showToast } from '../shared/index';

interface AvatarProfile {
  avatar_id: string;
  label?: string;
  category?: string | null;
  sort_index?: number;
}

interface MediaJob {
  id: string;
  status: string;
  mode: string;
  avatar_id: string;
  topic: string;
  category: string | null;
  script_mode?: string;
  script_text: string | null;
  video_url: string | null;
  avg_video_id: string | null;
  error: string | null;
  created_at: number;
}

interface StreamSession {
  id: string;
  status: string;
  mode: string;
  avatar_id: string;
  topic: string;
  category: string | null;
  script_mode?: string;
  script_text: string | null;
  hls_url: string | null;
  player_url: string | null;
  rtmp_enabled: boolean;
  rtmp_target_redacted: string | null;
  rtmp_active: boolean;
  error: string | null;
  created_at: number;
}

type ScriptMode = 'curator' | 'llm';
type DualDialogMode = 'scripted' | 'llm_turns';
type DualSpeakerId = 'avatar_a' | 'avatar_b';
type AudienceModeration = 'manual' | 'auto';

interface DualDialogLine {
  speaker_id: DualSpeakerId;
  text: string;
}

interface DualAvatarDialogJob {
  id: string;
  status: string;
  dialog_mode: DualDialogMode;
  render_mode: string;
  avatar_a_id: string;
  avatar_b_id: string;
  topic: string;
  category: string | null;
  role_a: string | null;
  role_b: string | null;
  max_turns: number | null;
  lines: DualDialogLine[];
  video_url: string | null;
  avg_video_id: string | null;
  turns: Array<{
    index: number;
    speaker_id: DualSpeakerId;
    status: string;
    error?: string | null;
  }>;
  turns_total: number;
  turns_done: number;
  error: string | null;
  created_at: number;
}

interface DualStreamSession {
  id: string;
  status: string;
  mode: string;
  avatar_a_id: string;
  avatar_b_id: string;
  topic: string;
  category: string | null;
  dialog_mode: DualDialogMode;
  role_a: string | null;
  role_b: string | null;
  lines: DualDialogLine[];
  turns: Array<{
    index: number;
    speaker_id: DualSpeakerId;
    status: string;
    error?: string | null;
  }>;
  turns_total: number;
  turns_done: number;
  question_moderation: AudienceModeration;
  hls_url: string | null;
  player_url: string | null;
  rtmp_enabled: boolean;
  rtmp_target_redacted: string | null;
  rtmp_active: boolean;
  clips_appended: number;
  error: string | null;
  created_at: number;
}

interface AudienceQuestion {
  id: string;
  session_id: string;
  source: string;
  text: string;
  author: string | null;
  status: string;
  moderation_mode: string;
  answer_speaker_id: DualSpeakerId | null;
  answer_text: string | null;
  clip_video_url: string | null;
  error: string | null;
}

/** MediaJob и DualAvatarDialogJob используют один и тот же набор статусов. */
const JOB_STATUS_LABELS: Record<string, string> = {
  queued: 'В очереди',
  running: 'Рендерится',
  completed: 'Готово',
  failed: 'Ошибка',
  rejected_capacity: 'Лимит (отклонено)',
};

/** StreamSession и DualStreamSession используют один и тот же набор статусов. */
const STREAM_STATUS_LABELS: Record<string, string> = {
  queued: 'В очереди',
  starting: 'Запускается',
  live: 'В эфире',
  ended: 'Остановлен',
  failed: 'Ошибка',
  rejected_capacity: 'Лимит (отклонено)',
};

const STORAGE_PREFIX = 'abcast.';
const STORAGE_KEYS = {
  job: `${STORAGE_PREFIX}jobId`,
  stream: `${STORAGE_PREFIX}streamId`,
  dualJob: `${STORAGE_PREFIX}dualJobId`,
  dualStream: `${STORAGE_PREFIX}dualStreamId`,
} as const;

let profilesCache: AvatarProfile[] = [];
let categoriesCache: string[] = [];
/** История записей (DM-HIST): последние N штук каждого типа, новые первыми. */
let mediaJobsHistory: MediaJob[] = [];
let streamSessionsHistory: StreamSession[] = [];
let dualJobsHistory: DualAvatarDialogJob[] = [];
let dualStreamsHistory: DualStreamSession[] = [];
const HISTORY_LIMIT = 20;
let eventsBound = false;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let dualPollTimer: ReturnType<typeof setTimeout> | null = null;
let dstreamPollTimer: ReturnType<typeof setTimeout> | null = null;
let activeJobId: string | null = null;
let activeStreamId: string | null = null;
let activeDualJobId: string | null = null;
let activeDualStreamId: string | null = null;
/** DualStreamSession в статусе live — иначе Q&A ingest даёт 409. */
let dstreamIsLive = false;
let lastFetchError: string | null = null;
let dualLinesBound = false;
let dstreamLinesBound = false;

function readStoredId(key: string): string | null {
  try {
    const v = sessionStorage.getItem(key)?.trim();
    return v || null;
  } catch {
    return null;
  }
}

function writeStoredId(key: string, id: string | null): void {
  try {
    if (id) sessionStorage.setItem(key, id);
    else sessionStorage.removeItem(key);
  } catch {
    /* private mode / quota — ignore */
  }
}

function setActiveJobId(id: string | null): void {
  activeJobId = id;
  writeStoredId(STORAGE_KEYS.job, id);
}

function setActiveStreamId(id: string | null): void {
  activeStreamId = id;
  writeStoredId(STORAGE_KEYS.stream, id);
}

function setActiveDualJobId(id: string | null): void {
  activeDualJobId = id;
  writeStoredId(STORAGE_KEYS.dualJob, id);
}

function setActiveDualStreamId(id: string | null): void {
  activeDualStreamId = id;
  writeStoredId(STORAGE_KEYS.dualStream, id);
}

function profileCategory(avatarId: string): string {
  return (profilesCache.find((p) => p.avatar_id === avatarId)?.category || '').trim();
}

function setQaControlsEnabled(enabled: boolean): void {
  const submit = $<HTMLButtonElement>('btn-admin-abcast-dstream-q-submit');
  const text = $<HTMLTextAreaElement>('admin-abcast-dstream-q-text');
  const author = $<HTMLInputElement>('admin-abcast-dstream-q-author');
  if (submit) submit.disabled = !enabled;
  if (text) text.disabled = !enabled;
  if (author) author.disabled = !enabled;
}

function escapeHtml(text: string): string {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function authHeaders(json = false): Record<string, string> {
  const headers: Record<string, string> = { Authorization: 'Bearer ' + state.authToken };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

/** DM-UI-1: голый код статуса ничего не говорит куратору — 502 (шлюз/апстрим
    недоступен, обычно временно) получает такой же человеческий текст, как уже
    разобранный 429 (лимит GPU). Остальные коды — исходный `Ошибка … (${r.status})`. */
function httpStatusHint(status: number): string | null {
  if (status === 502) return 'Шлюз недоступен — апстрим не отвечает, попробуйте позже';
  return null;
}

/** DM-UI-4: блокирует кнопку на время её async-операции, чтобы двойной клик
    не отправил второй запрос поверх ещё не завершившегося первого. */
async function withBusyButton(id: string, fn: () => Promise<void>): Promise<void> {
  const btn = $<HTMLButtonElement>(id);
  if (btn) btn.disabled = true;
  try {
    await fn();
  } finally {
    if (btn) btn.disabled = false;
  }
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T | null> {
  lastFetchError = null;
  try {
    const r = await fetch(url, init);
    if (!r.ok) {
      const body = await r.json().catch(() => null) as { error?: { message?: string } } | null;
      lastFetchError = body?.error?.message || httpStatusHint(r.status) || `HTTP ${r.status}`;
      return null;
    }
    return await r.json() as T;
  } catch (err) {
    lastFetchError = err instanceof Error ? err.message : 'network_error';
    return null;
  }
}

function stopPolling(): void {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function stopDualPolling(): void {
  if (dualPollTimer) {
    clearTimeout(dualPollTimer);
    dualPollTimer = null;
  }
}

function stopDstreamPolling(): void {
  if (dstreamPollTimer) {
    clearTimeout(dstreamPollTimer);
    dstreamPollTimer = null;
  }
}

function setStatus(text: string): void {
  const el = $('admin-abcast-status');
  if (el) el.textContent = text;
}

function setDualStatus(text: string): void {
  const el = $('admin-abcast-dual-status');
  if (el) el.textContent = text;
}

function setDstreamStatus(text: string): void {
  const el = $('admin-abcast-dstream-status');
  if (el) el.textContent = text;
}

/** Единственный источник разметки бейджа статуса — используется и в текущем статусе, и в списках истории. */
function statusBadgeHtml(status: string, labels: Record<string, string>): string {
  const label = labels[status] || status;
  return `<span class="abcast-status-badge abcast-status-${escapeHtml(status)}">${escapeHtml(label)}</span>`;
}

/** Локализованный бейдж статуса + технические поля вторичным текстом (см. admin-avg-video.ts). */
function renderStatusBadge(
  elId: string,
  status: string,
  labels: Record<string, string>,
  detailLines: string[]
): void {
  const el = $(elId);
  if (!el) return;
  const badge = statusBadgeHtml(status, labels);
  const detail = detailLines.length
    ? `<div class="abcast-status-detail">${detailLines.map((l) => escapeHtml(l)).join('<br>')}</div>`
    : '';
  el.innerHTML = badge + detail;
}

/** Читаемая дата создания записи для строки истории (ru-RU, короткий формат). */
function formatHistoryDate(ts: number): string {
  if (!ts || !Number.isFinite(ts)) return '—';
  return new Date(ts).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Короткий вид id для строки истории — полный id остаётся в title-атрибуте. */
function shortHistoryId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

function setLinks(session: StreamSession | null, job: MediaJob | null): void {
  const box = $('admin-abcast-links');
  if (!box) return;
  const parts: string[] = [];
  if (session?.player_url) {
    parts.push(`<a href="${escapeHtml(session.player_url)}" target="_blank" rel="noopener">Свой плеер</a>`);
  }
  if (session?.hls_url) {
    parts.push(`<a href="${escapeHtml(session.hls_url)}" target="_blank" rel="noopener">HLS playlist</a>`);
  }
  if (session?.rtmp_target_redacted) {
    parts.push(`<span>RTMP: ${escapeHtml(session.rtmp_target_redacted)}${session.rtmp_active ? ' (active)' : ''}</span>`);
  }
  if (job?.avg_video_id) {
    parts.push(`<span>Видео в каталоге: ${escapeHtml(job.avg_video_id)}</span>`);
  }
  if (job?.video_url) {
    parts.push(`<a href="${escapeHtml(job.video_url)}" target="_blank" rel="noopener">mp4</a>`);
  }
  box.innerHTML = parts.join('');
}

function setDualLinks(job: DualAvatarDialogJob | null): void {
  const box = $('admin-abcast-dual-links');
  if (!box) return;
  const parts: string[] = [];
  if (job?.avg_video_id) {
    parts.push(`<span>Видео в каталоге: ${escapeHtml(job.avg_video_id)}</span>`);
  }
  if (job?.video_url) {
    parts.push(`<a href="${escapeHtml(job.video_url)}" target="_blank" rel="noopener">mp4</a>`);
  }
  box.innerHTML = parts.join('');
}

function setDstreamLinks(session: DualStreamSession | null): void {
  const box = $('admin-abcast-dstream-links');
  if (!box) return;
  const parts: string[] = [];
  if (session?.player_url) {
    parts.push(`<a href="${escapeHtml(session.player_url)}" target="_blank" rel="noopener">Свой плеер</a>`);
  }
  if (session?.hls_url) {
    parts.push(`<a href="${escapeHtml(session.hls_url)}" target="_blank" rel="noopener">HLS playlist</a>`);
  }
  if (session?.rtmp_target_redacted) {
    parts.push(`<span>RTMP: ${escapeHtml(session.rtmp_target_redacted)}${session.rtmp_active ? ' (active)' : ''}</span>`);
  }
  if (session) {
    parts.push(`<span>Клипов Q&amp;A: ${session.clips_appended}</span>`);
  }
  box.innerHTML = parts.join('');
}

function setPlayerFrame(playerUrl: string | null): void {
  const frame = $<HTMLIFrameElement>('admin-abcast-player-frame');
  if (!frame) return;
  if (playerUrl) {
    frame.src = playerUrl;
    frame.classList.remove('hidden');
  } else {
    frame.removeAttribute('src');
    frame.classList.add('hidden');
  }
}

function setDstreamPlayerFrame(playerUrl: string | null): void {
  const frame = $<HTMLIFrameElement>('admin-abcast-dstream-player-frame');
  if (!frame) return;
  if (playerUrl) {
    frame.src = playerUrl;
    frame.classList.remove('hidden');
  } else {
    frame.removeAttribute('src');
    frame.classList.add('hidden');
  }
}

function profileOptionsHtml(): string {
  const sorted = [...profilesCache].sort((a, b) =>
    (a.sort_index ?? 0) - (b.sort_index ?? 0)
    || (a.label || a.avatar_id).localeCompare(b.label || b.avatar_id, 'ru')
  );
  return sorted.length
    ? sorted.map((p) =>
      `<option value="${escapeHtml(p.avatar_id)}">${escapeHtml(p.label || p.avatar_id)}</option>`
    ).join('')
    : '<option value="">— нет профилей —</option>';
}

function categoryOptionsHtml(): string {
  return `<option value="">— из профиля аватара —</option>`
    + categoriesCache.map((name) =>
      `<option value="${escapeHtml(name)}">${escapeHtml(name)}</option>`
    ).join('');
}

function fillSelect(id: string, html: string): void {
  const sel = $<HTMLSelectElement>(id);
  if (sel) sel.innerHTML = html;
}

function populateSelects(): void {
  const profileHtml = profileOptionsHtml();
  const catHtml = categoryOptionsHtml();
  fillSelect('admin-abcast-avatar', profileHtml);
  fillSelect('admin-abcast-category', catHtml);
  fillSelect('admin-abcast-dual-avatar-a', profileHtml);
  fillSelect('admin-abcast-dual-avatar-b', profileHtml);
  fillSelect('admin-abcast-dual-category', catHtml);
  fillSelect('admin-abcast-dstream-avatar-a', profileHtml);
  fillSelect('admin-abcast-dstream-avatar-b', profileHtml);
  fillSelect('admin-abcast-dstream-category', catHtml);

  // Prefer different defaults for A/B when ≥2 profiles
  const sorted = [...profilesCache].sort((a, b) =>
    (a.sort_index ?? 0) - (b.sort_index ?? 0)
  );
  if (sorted.length >= 2) {
    const a = $<HTMLSelectElement>('admin-abcast-dual-avatar-a');
    const b = $<HTMLSelectElement>('admin-abcast-dual-avatar-b');
    const da = $<HTMLSelectElement>('admin-abcast-dstream-avatar-a');
    const db = $<HTMLSelectElement>('admin-abcast-dstream-avatar-b');
    if (a) a.value = sorted[0].avatar_id;
    if (b) b.value = sorted[1].avatar_id;
    if (da) da.value = sorted[0].avatar_id;
    if (db) db.value = sorted[1].avatar_id;
  }
}

function currentScriptMode(): ScriptMode {
  const v = ($<HTMLSelectElement>('admin-abcast-script-mode')?.value || 'llm').trim();
  return v === 'curator' ? 'curator' : 'llm';
}

function syncScriptModeUi(): void {
  const mode = currentScriptMode();
  const isCurator = mode === 'curator';
  const script = $<HTMLTextAreaElement>('admin-abcast-script');
  if (script) {
    script.disabled = !isCurator;
    if (isCurator) {
      script.setAttribute('required', 'required');
    } else {
      script.removeAttribute('required');
    }
  }
  // Подсветка обязательного поля в режиме куратора (иначе граница не отличима от опциональных полей).
  $('admin-abcast-script-wrap')?.classList.toggle('abcast-script-required', isCurator);
}

function readForm(): {
  avatar_id: string;
  topic: string;
  category: string;
  script_mode: ScriptMode;
  script_text: string;
  rtmp_url: string;
  rtmp_key: string;
} {
  return {
    avatar_id: ($<HTMLSelectElement>('admin-abcast-avatar')?.value || '').trim(),
    topic: ($<HTMLTextAreaElement>('admin-abcast-topic')?.value || '').trim(),
    category: ($<HTMLSelectElement>('admin-abcast-category')?.value || '').trim(),
    script_mode: currentScriptMode(),
    script_text: ($<HTMLTextAreaElement>('admin-abcast-script')?.value || '').trim(),
    rtmp_url: ($<HTMLInputElement>('admin-abcast-rtmp-url')?.value || '').trim(),
    rtmp_key: ($<HTMLInputElement>('admin-abcast-rtmp-key')?.value || '').trim(),
  };
}

function schedulePoll(kind: 'job' | 'stream', id: string): void {
  stopPolling();
  pollTimer = setTimeout(() => {
    void (kind === 'job' ? refreshJob(id) : refreshStream(id));
  }, 2500);
}

async function refreshJob(id: string): Promise<void> {
  const data = await fetchJson<{ job: MediaJob }>(
    `/api/avg-broadcast/media-jobs/${encodeURIComponent(id)}`,
    { headers: authHeaders() }
  );
  if (!data?.job) {
    setStatus(`Не удалось получить статус MediaJob.${lastFetchError ? ` ${lastFetchError}` : ''}`);
    if (lastFetchError?.includes('404') || lastFetchError === 'HTTP 404') {
      setActiveJobId(null);
      return;
    }
    // Временный сбой сети — не бросаем отслеживание, пробуем ещё раз.
    schedulePoll('job', id);
    return;
  }
  const job = data.job;
  setActiveJobId(job.id);
  renderStatusBadge('admin-abcast-status', job.status, JOB_STATUS_LABELS, [
    `MediaJob ${job.id}`,
    `mode=${job.mode} · script=${job.script_mode || 'llm'}`,
    job.category ? `category=${job.category}` : '',
    job.error ? `error: ${job.error}` : '',
    job.script_text ? `script: ${job.script_text.slice(0, 280)}${job.script_text.length > 280 ? '…' : ''}` : '',
  ].filter(Boolean));
  setLinks(null, job);
  setPlayerFrame(null);
  if (job.status === 'queued' || job.status === 'running') {
    schedulePoll('job', id);
  }
}

async function refreshStream(id: string): Promise<void> {
  const data = await fetchJson<{ session: StreamSession }>(
    `/api/avg-broadcast/stream-sessions/${encodeURIComponent(id)}`,
    { headers: authHeaders() }
  );
  if (!data?.session) {
    setStatus(`Не удалось получить статус StreamSession.${lastFetchError ? ` ${lastFetchError}` : ''}`);
    if (lastFetchError?.includes('404') || lastFetchError === 'HTTP 404') {
      setActiveStreamId(null);
      return;
    }
    // Временный сбой сети — не бросаем отслеживание, пробуем ещё раз.
    schedulePoll('stream', id);
    return;
  }
  const session = data.session;
  setActiveStreamId(session.id);
  renderStatusBadge('admin-abcast-status', session.status, STREAM_STATUS_LABELS, [
    `StreamSession ${session.id}`,
    `mode=${session.mode} · script=${session.script_mode || 'llm'}`,
    session.rtmp_enabled ? `rtmp=${session.rtmp_target_redacted || '—'} active=${session.rtmp_active}` : 'rtmp=off',
    session.error ? `error: ${session.error}` : '',
    session.script_text ? `script: ${session.script_text.slice(0, 280)}${session.script_text.length > 280 ? '…' : ''}` : '',
  ].filter(Boolean));
  setLinks(session, null);
  setPlayerFrame(session.player_url);
  if (session.status === 'queued' || session.status === 'starting' || session.status === 'live') {
    if (session.status !== 'live' || session.rtmp_active) {
      schedulePoll('stream', id);
    }
  }
}

function scriptPayload(form: ReturnType<typeof readForm>): {
  avatar_id: string;
  topic: string;
  category?: string;
  script_mode: ScriptMode;
  script_text?: string;
} {
  const body: {
    avatar_id: string;
    topic: string;
    category?: string;
    script_mode: ScriptMode;
    script_text?: string;
  } = {
    avatar_id: form.avatar_id,
    topic: form.topic,
    script_mode: form.script_mode,
  };
  if (form.category) body.category = form.category;
  if (form.script_mode === 'curator') body.script_text = form.script_text;
  return body;
}

async function startJob(): Promise<void> {
  const form = readForm();
  if (!form.avatar_id || !form.topic) {
    showToast('Укажите аватар и тему', { variant: 'error' });
    return;
  }
  if (form.script_mode === 'curator' && !form.script_text) {
    showToast('В режиме куратора укажите текст озвучки', { variant: 'error' });
    return;
  }
  stopPolling();
  setStatus('Запуск MediaJob…');
  setPlayerFrame(null);
  const r = await fetch('/api/avg-broadcast/media-jobs', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(scriptPayload(form)),
  });
  const data = await r.json().catch(() => null) as {
    job?: MediaJob;
    capacity_rejected?: boolean;
    error?: { message?: string };
  } | null;
  if (!data?.job) {
    const msg = data?.error?.message || httpStatusHint(r.status) || `Ошибка MediaJob (${r.status})`;
    showToast(msg, { variant: 'error' });
    setStatus(msg);
    return;
  }
  if (data.capacity_rejected) {
    showToast('Лимит рендеров/эфиров — подождите', { variant: 'error' });
  } else if (data.job.status === 'completed') {
    showToast('Ролик готов');
  } else if (data.job.status === 'failed') {
    showToast('MediaJob завершился с ошибкой', { variant: 'error' });
  }
  setActiveJobId(data.job.id);
  await refreshJob(data.job.id);
}

async function startStream(): Promise<void> {
  const form = readForm();
  if (!form.avatar_id || !form.topic) {
    showToast('Укажите аватар и тему', { variant: 'error' });
    return;
  }
  if (form.script_mode === 'curator' && !form.script_text) {
    showToast('В режиме куратора укажите текст озвучки', { variant: 'error' });
    return;
  }
  stopPolling();
  setStatus('Запуск эфира…');
  setPlayerFrame(null);
  const r = await fetch('/api/avg-broadcast/stream-sessions', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({
      ...scriptPayload(form),
      rtmp_url: form.rtmp_url || undefined,
      rtmp_key: form.rtmp_key || undefined,
    }),
  });
  const data = await r.json().catch(() => null) as {
    session?: StreamSession;
    capacity_rejected?: boolean;
    error?: { message?: string };
  } | null;
  if (!data?.session) {
    const msg = data?.error?.message || httpStatusHint(r.status) || `Ошибка эфира (${r.status})`;
    showToast(msg, { variant: 'error' });
    setStatus(msg);
    return;
  }
  if (data.capacity_rejected) {
    showToast('Лимит эфиров/рендеров — подождите', { variant: 'error' });
  } else if (data.session.status === 'live') {
    showToast('Эфир в эфире (HLS)');
  } else if (data.session.status === 'failed') {
    showToast('Эфир завершился с ошибкой', { variant: 'error' });
  }
  setActiveStreamId(data.session.id);
  await refreshStream(data.session.id);
}

async function stopStream(): Promise<void> {
  const id = activeStreamId || '';
  if (!id) {
    showToast('Нет активного эфира', { variant: 'error' });
    return;
  }
  stopPolling();
  const r = await fetch(`/api/avg-broadcast/stream-sessions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  const data = await r.json().catch(() => null) as { session?: StreamSession; error?: { message?: string } } | null;
  if (!r.ok || !data?.session) {
    showToast(data?.error?.message || httpStatusHint(r.status) || `Стоп не удался (${r.status})`, { variant: 'error' });
    return;
  }
  showToast('Эфир остановлен');
  setActiveStreamId(data.session.id);
  await refreshStream(data.session.id);
}

/* ---------- Dual line editors (shared pattern) ---------- */

function lineRowHtml(prefix: 'dual' | 'dstream', speaker: DualSpeakerId, text: string): string {
  return `
    <div class="abcast-dual-line-row" data-abcast-line-row>
      <select class="setup-input abcast-line-speaker" aria-label="Спикер реплики">
        <option value="avatar_a"${speaker === 'avatar_a' ? ' selected' : ''}>Аватар A</option>
        <option value="avatar_b"${speaker === 'avatar_b' ? ' selected' : ''}>Аватар B</option>
      </select>
      <textarea class="setup-input abcast-line-text" rows="2" placeholder="Текст реплики">${escapeHtml(text)}</textarea>
      <button type="button" class="nav-btn btn-secondary abcast-dual-line-remove" data-abcast-line-remove aria-label="Удалить реплику">✕</button>
    </div>`;
}

function ensureLinesList(listId: string, prefix: 'dual' | 'dstream'): void {
  const list = $(listId);
  if (!list) return;
  if (!list.querySelector('[data-abcast-line-row]')) {
    list.innerHTML = lineRowHtml(prefix, 'avatar_a', '') + lineRowHtml(prefix, 'avatar_b', '');
  }
}

function readLinesFromList(listId: string): DualDialogLine[] {
  const list = $(listId);
  if (!list) return [];
  const rows = [...list.querySelectorAll('[data-abcast-line-row]')];
  const lines: DualDialogLine[] = [];
  for (const row of rows) {
    const speaker = (row.querySelector('.abcast-line-speaker') as HTMLSelectElement | null)?.value;
    const text = ((row.querySelector('.abcast-line-text') as HTMLTextAreaElement | null)?.value || '').trim();
    if (!text) continue;
    const speaker_id: DualSpeakerId = speaker === 'avatar_b' ? 'avatar_b' : 'avatar_a';
    lines.push({ speaker_id, text });
  }
  return lines;
}

function bindLinesList(listId: string, addBtnId: string, boundFlag: 'dual' | 'dstream'): void {
  if (boundFlag === 'dual' && dualLinesBound) return;
  if (boundFlag === 'dstream' && dstreamLinesBound) return;
  if (boundFlag === 'dual') dualLinesBound = true;
  else dstreamLinesBound = true;

  ensureLinesList(listId, boundFlag);
  $(addBtnId)?.addEventListener('click', () => {
    const list = $(listId);
    if (!list) return;
    const count = list.querySelectorAll('[data-abcast-line-row]').length;
    const next: DualSpeakerId = count % 2 === 0 ? 'avatar_a' : 'avatar_b';
    list.insertAdjacentHTML('beforeend', lineRowHtml(boundFlag, next, ''));
  });
  $(listId)?.addEventListener('click', (ev) => {
    const t = ev.target as HTMLElement | null;
    if (!t?.closest('[data-abcast-line-remove]')) return;
    const row = t.closest('[data-abcast-line-row]');
    const list = $(listId);
    if (!row || !list) return;
    if (list.querySelectorAll('[data-abcast-line-row]').length <= 1) {
      showToast('Нужна хотя бы одна реплика', { variant: 'error' });
      return;
    }
    row.remove();
  });
}

function syncDualModeUi(
  modeSelectId: string,
  scriptedWrapId: string,
  llmWrapId: string
): void {
  const mode = ($<HTMLSelectElement>(modeSelectId)?.value || 'scripted').trim();
  const scripted = $(scriptedWrapId);
  const llm = $(llmWrapId);
  const isLlm = mode === 'llm_turns';
  scripted?.classList.toggle('hidden', isLlm);
  llm?.classList.toggle('hidden', !isLlm);
}

/* ---------- DualAvatarDialog offline ---------- */

function scheduleDualPoll(id: string): void {
  stopDualPolling();
  dualPollTimer = setTimeout(() => { void refreshDualJob(id); }, 2500);
}

async function refreshDualJob(id: string): Promise<void> {
  const data = await fetchJson<{ job: DualAvatarDialogJob }>(
    `/api/avg-broadcast/dual-dialog-jobs/${encodeURIComponent(id)}`,
    { headers: authHeaders() }
  );
  if (!data?.job) {
    setDualStatus(`Не удалось получить статус DualAvatarDialog.${lastFetchError ? ` ${lastFetchError}` : ''}`);
    if (lastFetchError?.includes('404') || lastFetchError === 'HTTP 404') {
      setActiveDualJobId(null);
      return;
    }
    // Временный сбой сети — не бросаем отслеживание, пробуем ещё раз.
    scheduleDualPoll(id);
    return;
  }
  const job = data.job;
  setActiveDualJobId(job.id);
  renderStatusBadge('admin-abcast-dual-status', job.status, JOB_STATUS_LABELS, [
    `DualAvatarDialog ${job.id}`,
    `dialog_mode=${job.dialog_mode} · render=${job.render_mode}`,
    `turns=${job.turns_done}/${job.turns_total} · A=${job.avatar_a_id} · B=${job.avatar_b_id}`,
    job.category ? `category=${job.category}` : '',
    job.error ? `error: ${job.error}` : '',
  ].filter(Boolean));
  setDualLinks(job);
  if (job.status === 'queued' || job.status === 'running') {
    scheduleDualPoll(id);
  }
}

async function startDualJob(): Promise<void> {
  const avatar_a_id = ($<HTMLSelectElement>('admin-abcast-dual-avatar-a')?.value || '').trim();
  const avatar_b_id = ($<HTMLSelectElement>('admin-abcast-dual-avatar-b')?.value || '').trim();
  const topic = ($<HTMLTextAreaElement>('admin-abcast-dual-topic')?.value || '').trim();
  const category = ($<HTMLSelectElement>('admin-abcast-dual-category')?.value || '').trim();
  const dialog_mode = (($<HTMLSelectElement>('admin-abcast-dual-dialog-mode')?.value || 'scripted').trim() === 'llm_turns'
    ? 'llm_turns'
    : 'scripted') as DualDialogMode;

  if (!avatar_a_id || !avatar_b_id) {
    showToast('Выберите аватаров A и B', { variant: 'error' });
    return;
  }
  if (avatar_a_id === avatar_b_id) {
    showToast('Аватары A и B должны отличаться', { variant: 'error' });
    return;
  }
  if (!topic) {
    showToast('Укажите тему / заголовок', { variant: 'error' });
    return;
  }

  const body: Record<string, unknown> = {
    avatar_a_id,
    avatar_b_id,
    topic,
    dialog_mode,
  };
  if (category) body.category = category;

  if (dialog_mode === 'scripted') {
    const lines = readLinesFromList('admin-abcast-dual-lines-list');
    if (lines.length < 1) {
      showToast('Добавьте хотя бы одну реплику', { variant: 'error' });
      return;
    }
    body.lines = lines;
  } else {
    const role_a = ($<HTMLInputElement>('admin-abcast-dual-role-a')?.value || '').trim();
    const role_b = ($<HTMLInputElement>('admin-abcast-dual-role-b')?.value || '').trim();
    const max_turns = Number(($<HTMLInputElement>('admin-abcast-dual-max-turns')?.value || '8').trim());
    if (!role_a || !role_b) {
      showToast('Укажите роли A и B для LLM-диалога', { variant: 'error' });
      return;
    }
    body.role_a = role_a;
    body.role_b = role_b;
    if (Number.isFinite(max_turns) && max_turns > 0) body.max_turns = max_turns;
  }

  stopDualPolling();
  setDualStatus('Запуск DualAvatarDialog…');
  setDualLinks(null);
  const r = await fetch('/api/avg-broadcast/dual-dialog-jobs', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => null) as {
    job?: DualAvatarDialogJob;
    capacity_rejected?: boolean;
    error?: { message?: string };
  } | null;
  if (!data?.job) {
    const msg = data?.error?.message || httpStatusHint(r.status) || `Ошибка dual-ролика (${r.status})`;
    showToast(msg, { variant: 'error' });
    setDualStatus(msg);
    return;
  }
  if (data.capacity_rejected || r.status === 429) {
    showToast('Лимит рендеров/эфиров (GPU ≤1) — подождите', { variant: 'error' });
  } else if (data.job.status === 'completed') {
    showToast('Dual-ролик готов');
  } else if (data.job.status === 'failed') {
    showToast('Dual-ролик завершился с ошибкой', { variant: 'error' });
  }
  setActiveDualJobId(data.job.id);
  await refreshDualJob(data.job.id);
}

/* ---------- DualStreamSession + Q&A ---------- */

function scheduleDstreamPoll(id: string): void {
  stopDstreamPolling();
  dstreamPollTimer = setTimeout(() => { void refreshDualStream(id); }, 3000);
}

async function refreshQuestions(sessionId: string): Promise<void> {
  const box = $('admin-abcast-dstream-q-list');
  if (!box) return;
  const data = await fetchJson<{ questions: AudienceQuestion[] }>(
    `/api/avg-broadcast/dual-stream-sessions/${encodeURIComponent(sessionId)}/questions`,
    { headers: authHeaders() }
  );
  const questions = data?.questions || [];
  if (!questions.length) {
    box.innerHTML = '<div class="audit-log-empty">Очередь пуста. Отправьте вопрос формой выше (сессия должна быть live).</div>';
    return;
  }
  box.innerHTML = questions.map((q) => {
    const pending = q.status === 'pending';
    // Одна escapeHtml на всю строку meta — двойное экранирование (было: escapeHtml(author) внутри
    // ещё раз через escapeHtml(meta)) ломало «Tom & Jerry» в «Tom &amp; Jerry» на экране.
    const meta = [
      q.status,
      q.source,
      q.author ? `автор=${q.author}` : '',
      q.answer_speaker_id ? `ответ=${q.answer_speaker_id}` : '',
    ].filter(Boolean).join(' · ');
    const answer = q.answer_text
      ? `<div class="abcast-qa-answer">${escapeHtml(q.answer_text)}</div>`
      : '';
    const err = q.error ? `<div class="abcast-qa-error">${escapeHtml(q.error)}</div>` : '';
    const actions = pending
      ? `<div class="abcast-qa-actions">
          <button type="button" class="nav-btn btn-save btn-sm" data-abcast-q-approve="${escapeHtml(q.id)}">✓ Одобрить</button>
          <button type="button" class="nav-btn btn-secondary btn-sm" data-abcast-q-reject="${escapeHtml(q.id)}">✕ Отклонить</button>
        </div>`
      : '';
    return `<article class="abcast-qa-card" data-status="${escapeHtml(q.status)}">
      <div class="abcast-qa-meta">${escapeHtml(meta)}</div>
      <div class="abcast-qa-text">${escapeHtml(q.text)}</div>
      ${answer}${err}${actions}
    </article>`;
  }).join('');
}

async function refreshDualStream(id: string): Promise<void> {
  const data = await fetchJson<{ session: DualStreamSession }>(
    `/api/avg-broadcast/dual-stream-sessions/${encodeURIComponent(id)}`,
    { headers: authHeaders() }
  );
  if (!data?.session) {
    setDstreamStatus(`Не удалось получить статус DualStreamSession.${lastFetchError ? ` ${lastFetchError}` : ''}`);
    dstreamIsLive = false;
    setQaControlsEnabled(false);
    if (lastFetchError?.includes('404') || lastFetchError === 'HTTP 404') {
      setActiveDualStreamId(null);
      return;
    }
    // Временный сбой сети — не бросаем отслеживание, пробуем ещё раз.
    scheduleDstreamPoll(id);
    return;
  }
  const session = data.session;
  setActiveDualStreamId(session.id);
  dstreamIsLive = session.status === 'live';
  setQaControlsEnabled(dstreamIsLive);
  renderStatusBadge('admin-abcast-dstream-status', session.status, STREAM_STATUS_LABELS, [
    `DualStreamSession ${session.id}`,
    `dialog_mode=${session.dialog_mode} · moderation=${session.question_moderation}`,
    `turns=${session.turns_done}/${session.turns_total} · clips_qa=${session.clips_appended}`,
    `A=${session.avatar_a_id} · B=${session.avatar_b_id}`,
    session.category ? `category=${session.category}` : 'category=— (нужна для Q&A)',
    session.rtmp_enabled
      ? `rtmp=${session.rtmp_target_redacted || '—'} active=${session.rtmp_active}`
      : 'rtmp=off',
    session.error ? `error: ${session.error}` : '',
    dstreamIsLive ? '' : 'Q&A: ждите status=live',
  ].filter(Boolean));
  setDstreamLinks(session);
  setDstreamPlayerFrame(session.player_url);
  if (session.status === 'live' || session.status === 'queued' || session.status === 'starting') {
    await refreshQuestions(session.id);
  }
  if (session.status === 'queued' || session.status === 'starting' || session.status === 'live') {
    scheduleDstreamPoll(id);
  }
}

async function startDualStream(): Promise<void> {
  const avatar_a_id = ($<HTMLSelectElement>('admin-abcast-dstream-avatar-a')?.value || '').trim();
  const avatar_b_id = ($<HTMLSelectElement>('admin-abcast-dstream-avatar-b')?.value || '').trim();
  const topic = ($<HTMLTextAreaElement>('admin-abcast-dstream-topic')?.value || '').trim();
  const category = ($<HTMLSelectElement>('admin-abcast-dstream-category')?.value || '').trim();
  const dialog_mode = (($<HTMLSelectElement>('admin-abcast-dstream-dialog-mode')?.value || 'scripted').trim() === 'llm_turns'
    ? 'llm_turns'
    : 'scripted') as DualDialogMode;
  const question_moderation = (($<HTMLSelectElement>('admin-abcast-dstream-moderation')?.value || 'manual').trim() === 'auto'
    ? 'auto'
    : 'manual') as AudienceModeration;
  const rtmp_url = ($<HTMLInputElement>('admin-abcast-dstream-rtmp-url')?.value || '').trim();
  const rtmp_key = ($<HTMLInputElement>('admin-abcast-dstream-rtmp-key')?.value || '').trim();

  if (!avatar_a_id || !avatar_b_id) {
    showToast('Выберите аватаров A и B', { variant: 'error' });
    return;
  }
  if (avatar_a_id === avatar_b_id) {
    showToast('Аватары A и B должны отличаться', { variant: 'error' });
    return;
  }
  if (!topic) {
    showToast('Укажите тему эфира', { variant: 'error' });
    return;
  }
  const resolvedCategory = category || profileCategory(avatar_a_id) || profileCategory(avatar_b_id);
  if (!resolvedCategory) {
    showToast('Нужна категория: укажите в форме или в профилях аватаров A/B (RAG и Q&A)', { variant: 'error' });
    return;
  }

  const body: Record<string, unknown> = {
    avatar_a_id,
    avatar_b_id,
    topic,
    dialog_mode,
    question_moderation,
    category: resolvedCategory,
  };
  if (rtmp_url) body.rtmp_url = rtmp_url;
  if (rtmp_key) body.rtmp_key = rtmp_key;

  if (dialog_mode === 'scripted') {
    const lines = readLinesFromList('admin-abcast-dstream-lines-list');
    if (lines.length < 1) {
      showToast('Добавьте хотя бы одну стартовую реплику', { variant: 'error' });
      return;
    }
    body.lines = lines;
  } else {
    const role_a = ($<HTMLInputElement>('admin-abcast-dstream-role-a')?.value || '').trim();
    const role_b = ($<HTMLInputElement>('admin-abcast-dstream-role-b')?.value || '').trim();
    const max_turns = Number(($<HTMLInputElement>('admin-abcast-dstream-max-turns')?.value || '8').trim());
    if (!role_a || !role_b) {
      showToast('Укажите роли A и B', { variant: 'error' });
      return;
    }
    body.role_a = role_a;
    body.role_b = role_b;
    if (Number.isFinite(max_turns) && max_turns > 0) body.max_turns = max_turns;
  }

  stopDstreamPolling();
  dstreamIsLive = false;
  setQaControlsEnabled(false);
  setDstreamStatus('Запуск DualStreamSession…');
  setDstreamPlayerFrame(null);
  setDstreamLinks(null);
  const r = await fetch('/api/avg-broadcast/dual-stream-sessions', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => null) as {
    session?: DualStreamSession;
    capacity_rejected?: boolean;
    error?: { message?: string };
  } | null;
  if (!data?.session) {
    const msg = data?.error?.message || httpStatusHint(r.status) || `Ошибка dual-эфира (${r.status})`;
    showToast(msg, { variant: 'error' });
    setDstreamStatus(msg);
    return;
  }
  if (data.capacity_rejected || r.status === 429) {
    showToast('Лимит эфиров/рендеров (GPU ≤1) — подождите', { variant: 'error' });
  } else if (data.session.status === 'live') {
    showToast('Dual-эфир в эфире (HLS)');
  } else if (data.session.status === 'failed') {
    showToast('Dual-эфир завершился с ошибкой', { variant: 'error' });
  }
  setActiveDualStreamId(data.session.id);
  await refreshDualStream(data.session.id);
}

async function stopDualStream(): Promise<void> {
  const id = activeDualStreamId || '';
  if (!id) {
    showToast('Нет активного dual-эфира', { variant: 'error' });
    return;
  }
  stopDstreamPolling();
  const r = await fetch(`/api/avg-broadcast/dual-stream-sessions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  const data = await r.json().catch(() => null) as {
    session?: DualStreamSession;
    error?: { message?: string };
  } | null;
  if (!r.ok || !data?.session) {
    showToast(data?.error?.message || httpStatusHint(r.status) || `Стоп dual-эфира не удался (${r.status})`, { variant: 'error' });
    return;
  }
  showToast('Dual-эфир остановлен');
  setActiveDualStreamId(data.session.id);
  await refreshDualStream(data.session.id);
}

async function submitAudienceQuestion(): Promise<void> {
  const id = activeDualStreamId || '';
  if (!id) {
    showToast('Сначала запустите dual-эфир', { variant: 'error' });
    return;
  }
  if (!dstreamIsLive) {
    showToast('Вопросы принимаются только при status=live — дождитесь эфира', { variant: 'error' });
    return;
  }
  const text = ($<HTMLTextAreaElement>('admin-abcast-dstream-q-text')?.value || '').trim();
  const author = ($<HTMLInputElement>('admin-abcast-dstream-q-author')?.value || '').trim();
  if (text.length < 5) {
    showToast('Текст вопроса: минимум 5 символов', { variant: 'error' });
    return;
  }
  const r = await fetch(
    `/api/avg-broadcast/dual-stream-sessions/${encodeURIComponent(id)}/questions`,
    {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ text, author: author || undefined }),
    }
  );
  const data = await r.json().catch(() => null) as {
    question?: AudienceQuestion;
    auto_moderated?: boolean;
    error?: { message?: string };
  } | null;
  if (!r.ok || !data?.question) {
    showToast(data?.error?.message || httpStatusHint(r.status) || `Вопрос не принят (${r.status})`, { variant: 'error' });
    return;
  }
  const q = data.question;
  if (q.status === 'answered') {
    showToast(data.auto_moderated ? 'Автоответ клипом добавлен в эфир' : 'Вопрос отвечен');
  } else if (q.status === 'pending') {
    showToast('Вопрос в очереди — одобрите или отклоните');
  } else if (q.status === 'auto_rejected') {
    showToast('Авто-фильтр отклонил вопрос');
  } else {
    showToast(`Статус: ${q.status}`);
  }
  const ta = $<HTMLTextAreaElement>('admin-abcast-dstream-q-text');
  if (ta) ta.value = '';
  await refreshQuestions(id);
  await refreshDualStream(id);
}

async function moderateQuestion(
  qid: string,
  action: 'approve' | 'reject',
  btn?: HTMLButtonElement | null
): Promise<void> {
  const id = activeDualStreamId || '';
  if (!id) return;
  // approve на сервере синхронно ждёт LLM + рендер клипа — блокируем обе кнопки строки,
  // чтобы куратор не отправил повторный запрос, пока первый ещё летит.
  const row = btn?.closest('.abcast-qa-actions');
  const buttons = row
    ? [...row.querySelectorAll<HTMLButtonElement>('button')]
    : (btn ? [btn] : []);
  buttons.forEach((b) => { b.disabled = true; });
  try {
    showToast(action === 'approve' ? 'Генерация ответа и клипа…' : 'Отклонение…');
    const r = await fetch(
      `/api/avg-broadcast/dual-stream-sessions/${encodeURIComponent(id)}/questions/${encodeURIComponent(qid)}/${action}`,
      {
        method: 'POST',
        headers: authHeaders(true),
        body: action === 'reject' ? JSON.stringify({ reason: 'rejected_by_curator' }) : '{}',
      }
    );
    const data = await r.json().catch(() => null) as {
      question?: AudienceQuestion;
      error?: { message?: string };
    } | null;
    if (!r.ok || !data?.question) {
      showToast(data?.error?.message || httpStatusHint(r.status) || `Модерация не удалась (${r.status})`, { variant: 'error' });
      return;
    }
    if (action === 'approve' && data.question.status === 'answered') {
      showToast('Ответ-клип добавлен в HLS');
    } else if (action === 'reject') {
      showToast('Вопрос отклонён');
    }
    await refreshQuestions(id);
    await refreshDualStream(id);
  } finally {
    buttons.forEach((b) => { b.disabled = false; });
  }
}

/* ---------- История записей (DM-HIST): списки последних job/session по типу ---------- */

function renderHistoryList<T>(
  containerId: string,
  items: T[],
  rowHtml: (item: T) => string,
  emptyText: string
): void {
  const box = $(containerId);
  if (!box) return;
  box.innerHTML = items.length
    ? items.map(rowHtml).join('')
    : `<div class="audit-log-empty">${escapeHtml(emptyText)}</div>`;
}

function jobHistoryRowHtml(job: MediaJob): string {
  return `<button type="button" class="abcast-hist-row" data-abcast-select-job="${escapeHtml(job.id)}" title="${escapeHtml(job.id)}">
    ${statusBadgeHtml(job.status, JOB_STATUS_LABELS)}
    <span class="abcast-hist-time">${escapeHtml(formatHistoryDate(job.created_at))}</span>
    <span class="abcast-hist-topic">${escapeHtml(job.topic)}</span>
    <span class="abcast-hist-id">${escapeHtml(shortHistoryId(job.id))}</span>
  </button>`;
}

function streamHistoryRowHtml(session: StreamSession): string {
  return `<button type="button" class="abcast-hist-row" data-abcast-select-stream="${escapeHtml(session.id)}" title="${escapeHtml(session.id)}">
    ${statusBadgeHtml(session.status, STREAM_STATUS_LABELS)}
    <span class="abcast-hist-time">${escapeHtml(formatHistoryDate(session.created_at))}</span>
    <span class="abcast-hist-topic">${escapeHtml(session.topic)}</span>
    <span class="abcast-hist-id">${escapeHtml(shortHistoryId(session.id))}</span>
  </button>`;
}

function dualJobHistoryRowHtml(job: DualAvatarDialogJob): string {
  return `<button type="button" class="abcast-hist-row" data-abcast-select-dual-job="${escapeHtml(job.id)}" title="${escapeHtml(job.id)}">
    ${statusBadgeHtml(job.status, JOB_STATUS_LABELS)}
    <span class="abcast-hist-time">${escapeHtml(formatHistoryDate(job.created_at))}</span>
    <span class="abcast-hist-topic">${escapeHtml(job.topic)}</span>
    <span class="abcast-hist-id">${escapeHtml(shortHistoryId(job.id))}</span>
  </button>`;
}

function dualStreamHistoryRowHtml(session: DualStreamSession): string {
  return `<button type="button" class="abcast-hist-row" data-abcast-select-dual-stream="${escapeHtml(session.id)}" title="${escapeHtml(session.id)}">
    ${statusBadgeHtml(session.status, STREAM_STATUS_LABELS)}
    <span class="abcast-hist-time">${escapeHtml(formatHistoryDate(session.created_at))}</span>
    <span class="abcast-hist-topic">${escapeHtml(session.topic)}</span>
    <span class="abcast-hist-id">${escapeHtml(shortHistoryId(session.id))}</span>
  </button>`;
}

async function loadMediaJobsHistory(): Promise<void> {
  const data = await fetchJson<{ jobs: MediaJob[] }>(
    `/api/avg-broadcast/media-jobs?limit=${HISTORY_LIMIT}`,
    { headers: authHeaders() }
  );
  if (!data) {
    renderHistoryList('admin-abcast-jobs-list', [] as MediaJob[], jobHistoryRowHtml, 'Ошибка загрузки истории роликов.');
    showToast(`Не удалось загрузить историю роликов.${lastFetchError ? ` ${lastFetchError}` : ''}`, { variant: 'error' });
    return;
  }
  mediaJobsHistory = data.jobs || [];
  renderHistoryList('admin-abcast-jobs-list', mediaJobsHistory, jobHistoryRowHtml, 'Роликов пока нет.');
}

async function loadStreamSessionsHistory(): Promise<void> {
  const data = await fetchJson<{ sessions: StreamSession[] }>(
    `/api/avg-broadcast/stream-sessions?limit=${HISTORY_LIMIT}`,
    { headers: authHeaders() }
  );
  if (!data) {
    renderHistoryList('admin-abcast-streams-list', [] as StreamSession[], streamHistoryRowHtml, 'Ошибка загрузки истории эфиров.');
    showToast(`Не удалось загрузить историю эфиров.${lastFetchError ? ` ${lastFetchError}` : ''}`, { variant: 'error' });
    return;
  }
  streamSessionsHistory = data.sessions || [];
  renderHistoryList('admin-abcast-streams-list', streamSessionsHistory, streamHistoryRowHtml, 'Эфиров пока нет.');
}

async function loadDualJobsHistory(): Promise<void> {
  const data = await fetchJson<{ jobs: DualAvatarDialogJob[] }>(
    `/api/avg-broadcast/dual-dialog-jobs?limit=${HISTORY_LIMIT}`,
    { headers: authHeaders() }
  );
  if (!data) {
    renderHistoryList('admin-abcast-dual-jobs-list', [] as DualAvatarDialogJob[], dualJobHistoryRowHtml, 'Ошибка загрузки истории dual-роликов.');
    showToast(`Не удалось загрузить историю dual-роликов.${lastFetchError ? ` ${lastFetchError}` : ''}`, { variant: 'error' });
    return;
  }
  dualJobsHistory = data.jobs || [];
  renderHistoryList('admin-abcast-dual-jobs-list', dualJobsHistory, dualJobHistoryRowHtml, 'Dual-роликов пока нет.');
}

async function loadDualStreamsHistory(): Promise<void> {
  const data = await fetchJson<{ sessions: DualStreamSession[] }>(
    `/api/avg-broadcast/dual-stream-sessions?limit=${HISTORY_LIMIT}`,
    { headers: authHeaders() }
  );
  if (!data) {
    renderHistoryList('admin-abcast-dstream-sessions-list', [] as DualStreamSession[], dualStreamHistoryRowHtml, 'Ошибка загрузки истории dual-эфиров.');
    showToast(`Не удалось загрузить историю dual-эфиров.${lastFetchError ? ` ${lastFetchError}` : ''}`, { variant: 'error' });
    return;
  }
  dualStreamsHistory = data.sessions || [];
  renderHistoryList('admin-abcast-dstream-sessions-list', dualStreamsHistory, dualStreamHistoryRowHtml, 'Dual-эфиров пока нет.');
}

/** Клик по строке истории — делает запись активной и подтягивает подробности существующими refresh*-функциями. */
function selectJobFromHistory(id: string): void {
  stopPolling();
  setActiveJobId(id);
  void refreshJob(id);
}

function selectStreamFromHistory(id: string): void {
  stopPolling();
  setActiveStreamId(id);
  void refreshStream(id);
}

function selectDualJobFromHistory(id: string): void {
  stopDualPolling();
  setActiveDualJobId(id);
  void refreshDualJob(id);
}

function selectDualStreamFromHistory(id: string): void {
  stopDstreamPolling();
  setActiveDualStreamId(id);
  void refreshDualStream(id);
}

export async function loadAdminAvgBroadcast(): Promise<void> {
  const [profiles, categories] = await Promise.all([
    fetchJson<AvatarProfile[]>('/api/admin/avg-dialog/profiles', { headers: authHeaders() }),
    fetchJson<Record<string, unknown>>('/api/admin/categories', { headers: authHeaders() }),
  ]);
  profilesCache = profiles || [];
  categoriesCache = categories ? Object.keys(categories).sort() : [];
  populateSelects();
  syncScriptModeUi();
  syncDualModeUi(
    'admin-abcast-dual-dialog-mode',
    'admin-abcast-dual-scripted-wrap',
    'admin-abcast-dual-llm-wrap'
  );
  syncDualModeUi(
    'admin-abcast-dstream-dialog-mode',
    'admin-abcast-dstream-scripted-wrap',
    'admin-abcast-dstream-llm-wrap'
  );
  ensureLinesList('admin-abcast-dual-lines-list', 'dual');
  ensureLinesList('admin-abcast-dstream-lines-list', 'dstream');

  if (!activeJobId) activeJobId = readStoredId(STORAGE_KEYS.job);
  if (!activeStreamId) activeStreamId = readStoredId(STORAGE_KEYS.stream);
  if (!activeDualJobId) activeDualJobId = readStoredId(STORAGE_KEYS.dualJob);
  if (!activeDualStreamId) activeDualStreamId = readStoredId(STORAGE_KEYS.dualStream);

  if (activeStreamId) {
    await refreshStream(activeStreamId);
  } else if (activeJobId) {
    await refreshJob(activeJobId);
  } else {
    setStatus('Выберите аватар и тему. Режим «Свой текст» — textarea озвучки. Ролик → каталог «Видео». Эфир → HLS и опционально VK Live.');
    setLinks(null, null);
    setPlayerFrame(null);
  }
  if (activeDualJobId) {
    await refreshDualJob(activeDualJobId);
  } else {
    setDualStatus('Выберите двух аватаров и задайте сценарий или роли LLM.');
    setDualLinks(null);
  }
  if (activeDualStreamId) {
    await refreshDualStream(activeDualStreamId);
  } else {
    dstreamIsLive = false;
    setQaControlsEnabled(false);
    setDstreamStatus('Выберите двух аватаров, сценарий и режим модерации. Категория обязательна для Q&A.');
    setDstreamLinks(null);
    setDstreamPlayerFrame(null);
    const qList = $('admin-abcast-dstream-q-list');
    if (qList) {
      qList.innerHTML = '<div class="audit-log-empty">После старта dual-эфира (status=live) здесь появится очередь вопросов.</div>';
    }
  }

  // История записей (DM-HIST) — независима от активной записи выше, без автообновления.
  await Promise.all([
    loadMediaJobsHistory(),
    loadStreamSessionsHistory(),
    loadDualJobsHistory(),
    loadDualStreamsHistory(),
  ]);
}

export function bindAdminAvgBroadcastEvents(): void {
  if (eventsBound) return;
  eventsBound = true;

  $('admin-abcast-script-mode')?.addEventListener('change', () => { syncScriptModeUi(); });
  $('btn-admin-abcast-start-job')?.addEventListener('click', () => { void withBusyButton('btn-admin-abcast-start-job', startJob); });
  $('btn-admin-abcast-start-stream')?.addEventListener('click', () => { void withBusyButton('btn-admin-abcast-start-stream', startStream); });
  $('btn-admin-abcast-stop-stream')?.addEventListener('click', () => { void withBusyButton('btn-admin-abcast-stop-stream', stopStream); });
  $('btn-admin-abcast-refresh')?.addEventListener('click', () => {
    if (activeStreamId) void refreshStream(activeStreamId);
    else if (activeJobId) void refreshJob(activeJobId);
    else void loadAdminAvgBroadcast();
  });

  // История записей (DM-HIST): клик по строке делает запись активной, кнопка — обновляет список.
  $('admin-abcast-jobs-list')?.addEventListener('click', (ev) => {
    const row = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-abcast-select-job]');
    if (row?.dataset.abcastSelectJob) selectJobFromHistory(row.dataset.abcastSelectJob);
  });
  $('btn-admin-abcast-jobs-refresh')?.addEventListener('click', () => { void loadMediaJobsHistory(); });

  $('admin-abcast-streams-list')?.addEventListener('click', (ev) => {
    const row = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-abcast-select-stream]');
    if (row?.dataset.abcastSelectStream) selectStreamFromHistory(row.dataset.abcastSelectStream);
  });
  $('btn-admin-abcast-streams-refresh')?.addEventListener('click', () => { void loadStreamSessionsHistory(); });

  $('admin-abcast-dual-dialog-mode')?.addEventListener('change', () => {
    syncDualModeUi(
      'admin-abcast-dual-dialog-mode',
      'admin-abcast-dual-scripted-wrap',
      'admin-abcast-dual-llm-wrap'
    );
  });
  bindLinesList('admin-abcast-dual-lines-list', 'btn-admin-abcast-dual-add-line', 'dual');
  $('btn-admin-abcast-dual-start')?.addEventListener('click', () => { void withBusyButton('btn-admin-abcast-dual-start', startDualJob); });
  $('btn-admin-abcast-dual-refresh')?.addEventListener('click', () => {
    if (activeDualJobId) void refreshDualJob(activeDualJobId);
    else void loadAdminAvgBroadcast();
  });

  $('admin-abcast-dual-jobs-list')?.addEventListener('click', (ev) => {
    const row = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-abcast-select-dual-job]');
    if (row?.dataset.abcastSelectDualJob) selectDualJobFromHistory(row.dataset.abcastSelectDualJob);
  });
  $('btn-admin-abcast-dual-jobs-refresh')?.addEventListener('click', () => { void loadDualJobsHistory(); });

  $('admin-abcast-dstream-dialog-mode')?.addEventListener('change', () => {
    syncDualModeUi(
      'admin-abcast-dstream-dialog-mode',
      'admin-abcast-dstream-scripted-wrap',
      'admin-abcast-dstream-llm-wrap'
    );
  });
  bindLinesList('admin-abcast-dstream-lines-list', 'btn-admin-abcast-dstream-add-line', 'dstream');
  $('btn-admin-abcast-dstream-start')?.addEventListener('click', () => { void withBusyButton('btn-admin-abcast-dstream-start', startDualStream); });
  $('btn-admin-abcast-dstream-stop')?.addEventListener('click', () => { void withBusyButton('btn-admin-abcast-dstream-stop', stopDualStream); });
  $('btn-admin-abcast-dstream-refresh')?.addEventListener('click', () => {
    if (activeDualStreamId) void refreshDualStream(activeDualStreamId);
    else void loadAdminAvgBroadcast();
  });
  $('btn-admin-abcast-dstream-q-submit')?.addEventListener('click', () => {
    void submitAudienceQuestion();
  });
  $('btn-admin-abcast-dstream-q-refresh')?.addEventListener('click', () => {
    if (activeDualStreamId) void refreshQuestions(activeDualStreamId);
    else showToast('Нет активного dual-эфира', { variant: 'error' });
  });
  $('admin-abcast-dstream-q-list')?.addEventListener('click', (ev) => {
    const t = ev.target as HTMLElement | null;
    const approve = t?.closest('[data-abcast-q-approve]') as HTMLButtonElement | null;
    const reject = t?.closest('[data-abcast-q-reject]') as HTMLButtonElement | null;
    if (approve?.dataset.abcastQApprove) {
      void moderateQuestion(approve.dataset.abcastQApprove, 'approve', approve);
    } else if (reject?.dataset.abcastQReject) {
      void moderateQuestion(reject.dataset.abcastQReject, 'reject', reject);
    }
  });

  $('admin-abcast-dstream-sessions-list')?.addEventListener('click', (ev) => {
    const row = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-abcast-select-dual-stream]');
    if (row?.dataset.abcastSelectDualStream) selectDualStreamFromHistory(row.dataset.abcastSelectDualStream);
  });
  $('btn-admin-abcast-dstream-sessions-refresh')?.addEventListener('click', () => { void loadDualStreamsHistory(); });

  syncScriptModeUi();
  syncDualModeUi(
    'admin-abcast-dual-dialog-mode',
    'admin-abcast-dual-scripted-wrap',
    'admin-abcast-dual-llm-wrap'
  );
  syncDualModeUi(
    'admin-abcast-dstream-dialog-mode',
    'admin-abcast-dstream-scripted-wrap',
    'admin-abcast-dstream-llm-wrap'
  );

  // DM-UI-4: переключение под-вкладки админки гасит опрос через
  // stopAdminAvgBroadcastPolling, но уход из вкладки браузера (свернули окно,
  // переключились в другую вкладку) им не ловится — опрос продолжал бы бить
  // API впустую в фоне. visibilitychange покрывает оба случая одним слушателем.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopAdminAvgBroadcastPolling();
    } else {
      resumeAdminAvgBroadcastPolling();
    }
  });
}

/** DM-UI-4: возврат во вкладку браузера — если запись ещё активна и не в
    терминальном статусе, refresh* сам перезапустит свой setTimeout. */
function resumeAdminAvgBroadcastPolling(): void {
  if (activeStreamId) void refreshStream(activeStreamId);
  else if (activeJobId) void refreshJob(activeJobId);
  if (activeDualJobId) void refreshDualJob(activeDualJobId);
  if (activeDualStreamId) void refreshDualStream(activeDualStreamId);
}

/** Гасит все опросы вкладки (job/stream/dual/dstream) — вызывается при уходе со вкладки «Эфир». */
export function stopAdminAvgBroadcastPolling(): void {
  stopPolling();
  stopDualPolling();
  stopDstreamPolling();
}
